import * as XLSX from "xlsx";

/*
 * Shared helpers for the consolidated "products to deliver" result returned by
 * GET /salesOrder/getProductsToDeliver. The screen, the Excel export and the
 * print view all read the same structure so they always show the same figures.
 */

export const TITLE = "Liste consolidée des produits à livrer";

export const formatQty = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  return Number.isInteger(n) ? String(n) : String(parseFloat(n.toFixed(3)));
};

export const formatDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  // delivery dates are stored at UTC midnight
  const dd = String(d.getUTCDate()).padStart(2, "0");
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${d.getUTCFullYear()}`;
};

export const formatPeriodDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

// One note entry -> "text (BDC ref1, ref2)"
export const noteToText = (note) =>
  note.bdcRefs && note.bdcRefs.length
    ? `${note.text} (BDC ${note.bdcRefs.join(", ")})`
    : note.text;

export const notesToText = (notes, separator = "\n") =>
  (notes || []).map(noteToText).join(separator);

export const periodLabel = (result) =>
  result.label ||
  (result.dateFrom === result.dateTo
    ? `Le ${formatPeriodDate(result.dateFrom)}`
    : `Du ${formatPeriodDate(result.dateFrom)} au ${formatPeriodDate(result.dateTo)}`);

export const exportToExcel = (result) => {
  const generated = new Date().toLocaleString("fr-FR");
  const rows = [
    [TITLE],
    [`Période de livraison : ${periodLabel(result)}`],
    [`Généré le : ${generated}`],
    [],
  ];
  const merges = [];
  (result.categories || []).forEach((cat) => {
    rows.push([`Catégorie : ${cat.category}`]);
    rows.push(["Référence", "Libellé du produit", "Quantité totale", "Notes"]);
    cat.products.forEach((p) => {
      rows.push([p.reference || "", p.label || "", Number(p.quantity), notesToText(p.notes)]);
    });
    rows.push(["", `TOTAL ${String(cat.category).toUpperCase()}`, Number(cat.totalQuantity), ""]);
    rows.push([]);
  });
  rows.push(["", "TOTAL GÉNÉRAL", Number(result.totalQuantity), ""]);

  const sheet = XLSX.utils.aoa_to_sheet(rows);
  sheet["!cols"] = [{ wch: 18 }, { wch: 42 }, { wch: 16 }, { wch: 60 }];
  sheet["!merges"] = merges;

  const detailRows = [
    ["BDC", "Client", "Date de livraison", "Catégorie", "Référence", "Produit", "Quantité", "Note"],
    ...(result.details || []).map((d) => [
      d.bdcReference || "",
      d.client || "",
      formatDate(d.deliveryDate),
      d.category || "",
      d.productReference || "",
      d.productLabel || "",
      Number(d.quantity),
      d.note || "",
    ]),
  ];
  const detailSheet = XLSX.utils.aoa_to_sheet(detailRows);
  detailSheet["!cols"] = [
    { wch: 14 }, { wch: 30 }, { wch: 16 }, { wch: 24 }, { wch: 18 }, { wch: 40 }, { wch: 10 }, { wch: 50 },
  ];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, sheet, "Produits à livrer");
  XLSX.utils.book_append_sheet(wb, detailSheet, "Détail par BDC");
  XLSX.writeFile(wb, `produits-a-livrer_${result.dateFrom}_${result.dateTo}.xlsx`);
};

const esc = (v) =>
  String(v === null || v === undefined ? "" : v)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

export const printConsolidation = (result) => {
  const body = (result.categories || [])
    .map(
      (cat) => `
      <h3>Catégorie : ${esc(cat.category)}</h3>
      <table>
        <thead><tr><th style="width:18%">Référence</th><th>Libellé du produit</th><th style="width:12%" class="num">Quantité totale</th><th style="width:30%">Notes</th></tr></thead>
        <tbody>
          ${cat.products
            .map(
              (p) => `<tr><td>${esc(p.reference)}</td><td>${esc(p.label)}</td><td class="num">${formatQty(
                p.quantity
              )}</td><td>${(p.notes || []).map((n) => esc(noteToText(n))).join("<br/>")}</td></tr>`
            )
            .join("")}
          <tr class="total"><td></td><td>Total ${esc(cat.category)}</td><td class="num">${formatQty(
            cat.totalQuantity
          )}</td><td></td></tr>
        </tbody>
      </table>`
    )
    .join("");

  const win = window.open("", "PrintWindow", "width=900,height=700");
  if (!win) return false;
  win.document.write(`<!doctype html><html><head><meta charset="utf-8"/><title>${TITLE}</title>
  <style>
    @page { size: A4; margin: 14mm; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 11px; color: #000; }
    h1 { font-size: 16px; margin: 0 0 4px; }
    .meta { margin-bottom: 10px; }
    h3 { font-size: 12px; margin: 14px 0 4px; page-break-after: avoid; }
    table { width: 100%; border-collapse: collapse; }
    thead { display: table-header-group; }
    th, td { border: 1px solid #000; padding: 4px 6px; text-align: left; vertical-align: top; }
    th { background: #e6e6e6; }
    tr { page-break-inside: avoid; }
    .num { text-align: right; }
    .total td { font-weight: bold; background: #f2f2f2; }
    .grand { margin-top: 12px; font-weight: bold; font-size: 12px; }
  </style></head><body>
    <h1>${TITLE}</h1>
    <div class="meta">Période de livraison : ${esc(periodLabel(result))}<br/>Généré le : ${esc(
    new Date().toLocaleString("fr-FR")
  )}</div>
    ${body}
    <div class="grand">Total général : ${formatQty(result.totalQuantity)}</div>
  </body></html>`);
  win.document.close();
  win.focus();
  setTimeout(() => {
    win.print();
  }, 400);
  return true;
};
